import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, CreditCard, Ban, Lock, BadgeCheck, Layers, ChevronDown, Sparkles } from "lucide-react";
import { track } from "@/lib/analytics";
import { SectionHeading } from "@/components/marketing/SectionHeading";
import { NetworkField } from "@/components/marketing/NetworkField";

// Only truthful, verifiable signals — no testimonials/logos/metrics.
const TRUST = [
  { icon: CreditCard, label: "No card needed to explore the demo" },
  { icon: Ban, label: "Cancel anytime" },
  { icon: ShieldCheck, label: "Secure cloud infrastructure" },
  { icon: Lock, label: "Isolated demo data — no real customer information" },
  { icon: BadgeCheck, label: "UK payroll verified" },
  { icon: Layers, label: "One all-inclusive plan" },
];

const EASE = [0.16, 1, 0.3, 1];

const RR_CSS = `
.rr-orb{position:relative;width:44px;height:44px;border-radius:9999px;display:flex;align-items:center;justify-content:center;flex-shrink:0;background:radial-gradient(circle at 35% 28%,rgba(251,239,196,.22),rgba(212,175,55,.08) 55%,rgba(20,16,10,.92) 100%);box-shadow:inset 0 0 0 1px rgba(212,175,55,.55),inset 0 1px 0 rgba(255,255,255,.18),0 0 18px -4px rgba(212,175,55,.35),0 10px 18px -8px rgba(0,0,0,.8)}
.rr-spark{position:absolute;top:-1px;height:3px;width:120px;border-radius:9999px;background:linear-gradient(90deg,transparent,#FBEFC4,transparent);filter:blur(.6px);animation:rrRun 6s cubic-bezier(.45,0,.55,1) infinite}
@keyframes rrRun{0%{left:-10%;opacity:0}15%{opacity:1}85%{opacity:1}100%{left:100%;opacity:0}}
@media (prefers-reduced-motion: reduce){.rr-spark{animation:none}}
`;

const Hairline = ({ strong, delay = 0, spark }) => (
  <motion.div aria-hidden className="relative h-px w-full origin-center"
    style={{ background: `linear-gradient(90deg, transparent, rgba(212,175,55,${strong ? 0.55 : 0.3}), transparent)` }}
    initial={{ scaleX: 0, opacity: 0 }} whileInView={{ scaleX: 1, opacity: 1 }} viewport={{ once: true }} transition={{ duration: 1.4, ease: EASE, delay }}>
    {spark && <span className="rr-spark" />}
  </motion.div>
);

// Static trust ribbon: crisp gold-ringed icons that never move.
export function RiskReversalBand() {
  return (
    <section className="relative" data-testid="risk-reversal-band">
      <style>{RR_CSS}</style>
      <div className="absolute inset-0 pointer-events-none" style={{ WebkitMaskImage: "radial-gradient(70% 90% at 50% 50%, #000 30%, transparent 75%)", maskImage: "radial-gradient(70% 90% at 50% 50%, #000 30%, transparent 75%)" }}>
        <NetworkField count={14} speed={0.12} linkDist={0.42} style={{ opacity: 0.35 }} />
      </div>
      <div className="relative z-[1] max-w-6xl mx-auto px-5 sm:px-10 py-10 sm:py-12">
        <div className="mb-9"><Hairline strong spark /></div>
        <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-7 w-fit max-w-full mx-auto">
          {TRUST.map((t) => (
            <li key={t.label} data-testid="trust-item" className="flex items-center gap-3.5">
              <span className="rr-orb" data-testid="trust-icon">
                <t.icon className="w-[18px] h-[18px]" strokeWidth={1.6} style={{ color: "#F6E3A6" }} />
              </span>
              <span className="text-[13.5px] leading-tight font-medium tracking-tight text-white/90 max-w-[250px]">{t.label}</span>
            </li>
          ))}
        </ul>
        <div className="mt-9"><Hairline delay={0.3} /></div>
      </div>
    </section>
  );
}

const FAQ = [
  ["What does Admin Restaurant Intelligence actually include?", "Every module in one plan: workforce & rota, attendance and clock-in/out, payroll, the Training Academy, checklists, receipt intelligence, messaging, reporting and ARI — your living intelligence layer that reads across them all."],
  ["Can I explore the platform before subscribing?", "Yes. The interactive demo is the real product running on isolated sample data — explore it as an owner, no sign-up required."],
  ["Do I need a card to explore the demo?", "No. The demo needs no card and no account — just click Experience it."],
  ["Is the demo using the real platform?", "Yes. It runs the actual production application on seeded sample data, so what you see is what a paying customer receives."],
  ["What happens after I subscribe?", "You get your own Live Production account with the same capabilities you explored, ready to set up your real restaurant."],
  ["Can my managers and staff use the platform?", "Yes — there are owner, manager and staff experiences with role-appropriate access."],
  ["Which countries currently have verified payroll support?", "Payroll is fully verified for the UK and a set of additional countries, with more added over time. Other regions show as coming soon."],
  ["Can I cancel my subscription?", "Yes — you can cancel anytime."],
];

export function FaqSection() {
  const [open, setOpen] = useState(0);
  return (
    <section className="border-t border-white/5" data-testid="faq-section">
      <div className="max-w-3xl mx-auto px-5 sm:px-10 py-10 sm:py-14">
        <SectionHeading testId="faq-heading" className="mb-8" eyebrow="Questions, answered" lead="Everything you" accent="need to know." />
        <div className="divide-y divide-white/8">
          {FAQ.map(([q, a], i) => (
            <div key={i} className="py-1">
              <button data-testid={`faq-q-${i}`} onClick={() => setOpen(open === i ? -1 : i)}
                className="w-full flex items-center justify-between gap-4 py-5 text-left group">
                <span className="text-white group-hover:text-[#D4AF37] transition-colors duration-200 text-lg">{q}</span>
                <ChevronDown className={`w-5 h-5 shrink-0 text-white/40 transition-transform duration-300 ${open === i ? "rotate-180 text-[#D4AF37]" : ""}`} />
              </button>
              <div className={`grid transition-all duration-300 ease-out ${open === i ? "grid-rows-[1fr] opacity-100 pb-5" : "grid-rows-[0fr] opacity-0"}`}>
                <p className="overflow-hidden text-white/55 leading-relaxed text-sm">{a}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// Persistent, unobtrusive "Experience it" — appears after the visitor scrolls past the hero.
export function StickyExperienceCTA() {
  const navigate = useNavigate();
  const [show, setShow] = useState(false);
  useEffect(() => {
    const on = () => setShow(window.scrollY > 900);
    window.addEventListener("scroll", on, { passive: true });
    on();
    return () => window.removeEventListener("scroll", on);
  }, []);
  return (
    <button data-testid="sticky-experience-cta"
      onClick={() => { track("hero_cta", { source: "sticky" }); navigate("/experience"); }}
      className={`fixed z-[80] bottom-6 right-6 inline-flex items-center gap-2 rounded-full bg-[#D4AF37] text-[#0A0A0B] font-semibold px-6 py-3.5 shadow-[0_14px_44px_-8px_rgba(212,175,55,0.7)] transition-all duration-500 ${show ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 translate-y-6 pointer-events-none"}`}>
      <Sparkles className="w-4.5 h-4.5" /> Experience it
    </button>
  );
}
